import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { getPalette } from '../themePalette';
import {
  Sparkles, Image as ImageIcon, Bot, Wand2, Upload, Trash2, Send,
  ArrowLeft, Copy, Check, Loader2, Link2, Download,
} from 'lucide-react';

const BACKEND_ROOT = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
const API = BACKEND_ROOT + '/api';

const fullUrl = (u) => (!u ? '' : (u.startsWith('http') ? u : `${BACKEND_ROOT}${u}`));

const STYLE_PRESETS = [
  { label: 'Warm Modern', text: 'warm modern style, walnut wood tones, cream and taupe palette, soft brass accents, layered natural textures' },
  { label: 'Coastal', text: 'coastal style, airy whites and soft blues, natural linen, light oak, woven textures, breezy and bright' },
  { label: 'Transitional', text: 'transitional style, blend of classic and contemporary, neutral palette, clean lines with soft curves' },
  { label: 'Modern Farmhouse', text: 'modern farmhouse style, shiplap, matte black fixtures, reclaimed wood, warm neutrals' },
  { label: 'Organic Modern', text: 'organic modern style, earthy neutrals, plaster walls, rounded forms, natural stone and wood' },
  { label: 'Mid-Century', text: 'mid-century modern, teak wood, tapered legs, warm jewel-tone accents, clean retro lines' },
];

const TABS = [
  { key: 'render', label: 'Renderings', icon: ImageIcon },
  { key: 'assistant', label: 'Design Assistant', icon: Bot },
  { key: 'describe', label: 'FF&E Descriptions', icon: Wand2 },
];

export default function AIStudio() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const T = getPalette(theme);
  const [tab, setTab] = useState('render');
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    if (!projectId) return;
    fetch(`${API}/projects/${projectId}`)
      .then((r) => r.json())
      .then((d) => setProjectName(d?.name || d?.project?.name || ''))
      .catch(() => {});
  }, [projectId]);

  return (
    <div className="-mx-4 -my-6 min-h-screen px-4 py-6 sm:px-6" data-testid="ai-studio"
      style={{ background: T.page, color: T.text, fontFamily: "'Jost','Century Gothic',sans-serif" }}>
      <div className="mx-auto max-w-[1400px]">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button data-testid="ai-studio-back" onClick={() => navigate(projectId ? `/project/${projectId}` : '/')}
              className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: T.chip, border: `1px solid ${T.border}`, color: T.text2 }}>
              <ArrowLeft size={16} />
            </button>
            <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: T.goldGrad, color: T.onGold }}>
              <Sparkles size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: T.text }}>AI Studio</h1>
              <div className="text-xs" style={{ color: T.text2 }}>
                {projectName ? `For ${projectName}` : 'Company-wide'} · powered by GPT-6 Astra + Nano Banana
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`ai-tab-${t.key}`} onClick={() => setTab(t.key)}
                className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-all"
                style={active ? { background: T.goldGrad, color: T.onGold } : { background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>
                <t.icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'render' && <RenderTab T={T} projectId={projectId} />}
        {tab === 'assistant' && <AssistantTab T={T} projectId={projectId} />}
        {tab === 'describe' && <DescribeTab T={T} />}
      </div>
    </div>
  );
}

/* ------------------------------- RENDERINGS ------------------------------- */
const RenderTab = ({ T, projectId }) => {
  const [inputImage, setInputImage] = useState(null); // data URL or base64
  const [prompt, setPrompt] = useState('');
  const [roomName, setRoomName] = useState('');
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState('');
  const [renders, setRenders] = useState([]);
  const [canvaConnected, setCanvaConnected] = useState(false);
  const [canvaUrl, setCanvaUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const loadRenders = useCallback(() => {
    const q = projectId ? `?project_id=${projectId}` : '';
    fetch(`${API}/ai-studio/renders${q}`).then((r) => r.json())
      .then((d) => setRenders(d.renders || [])).catch(() => {});
  }, [projectId]);

  useEffect(() => { loadRenders(); }, [loadRenders]);
  useEffect(() => {
    fetch(`${API}/ai-studio/canva/status`).then((r) => r.json())
      .then((d) => setCanvaConnected(!!d.connected)).catch(() => {});
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setInputImage(reader.result);
    reader.readAsDataURL(f);
  };

  const connectCanva = async () => {
    try {
      const r = await fetch(`${API}/canva/auth`);
      const d = await r.json();
      const url = d.auth_url || d.url;
      if (url) window.location.href = url;
      else alert('Could not start Canva connection.');
    } catch (e) { alert('Could not start Canva connection.'); }
  };

  const importFromCanva = async () => {
    if (!canvaUrl.trim()) return;
    setImporting(true); setError('');
    try {
      const r = await fetch(`${API}/ai-studio/canva/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ canva_url: canvaUrl.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Import failed');
      setInputImage(`data:image/png;base64,${d.image_base64}`);
    } catch (e) { setError(String(e.message || e)); }
    finally { setImporting(false); }
  };

  const doRender = async () => {
    if (!prompt.trim() && !inputImage) { setError('Add a description or an image first.'); return; }
    setRendering(true); setError('');
    try {
      const r = await fetch(`${API}/ai-studio/render`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, project_id: projectId || null, room_name: roomName || null, input_image: inputImage || null }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.detail || 'Render failed');
      setRenders((prev) => [d, ...prev]);
    } catch (e) { setError(String(e.message || e)); }
    finally { setRendering(false); }
  };

  const del = async (id) => {
    await fetch(`${API}/ai-studio/renders/${id}`, { method: 'DELETE' }).catch(() => {});
    setRenders((prev) => prev.filter((x) => x.id !== id));
  };

  const [canvaBusy, setCanvaBusy] = useState(null);
  const sendToCanva = async (r) => {
    setCanvaBusy(r.id);
    try {
      const res = await fetch(`${API}/ai-studio/canva/send-asset`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: r.image_url, name: r.room_name || 'EstDesign Rendering' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      alert('✅ Sent to your Canva uploads — find it under "Uploads" in Canva.');
    } catch (e) { alert('Send to Canva failed: ' + (e.message || e)); }
    finally { setCanvaBusy(null); }
  };

  const applyPreset = (p) => setPrompt((prev) => (prev.trim() ? `${prev.trim()}, ${p.text}` : p.text));

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      {/* Controls */}
      <div className="lg:col-span-4">
        <div className="rounded-xl p-5" style={{ background: T.card, border: `1px solid ${T.border}` }}>
          <div className="mb-3 text-sm font-semibold" style={{ color: T.text }}>1 · Start from a picture (optional)</div>
          <p className="mb-3 text-xs" style={{ color: T.text2 }}>Upload a room photo or a Canva comp and the AI will restyle that exact layout into a photorealistic room. Skip to generate purely from text.</p>

          {inputImage ? (
            <div className="relative mb-3 overflow-hidden rounded-lg" style={{ border: `1px solid ${T.border}` }}>
              <img src={inputImage} alt="source" data-testid="render-input-preview" className="max-h-56 w-full object-contain" style={{ background: '#111' }} />
              <button data-testid="render-clear-input" onClick={() => setInputImage(null)}
                className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full text-white" style={{ background: 'rgba(0,0,0,0.6)' }}>×</button>
            </div>
          ) : (
            <button data-testid="render-upload-btn" onClick={() => fileRef.current?.click()}
              className="mb-3 flex w-full flex-col items-center justify-center gap-2 rounded-lg py-6 text-sm"
              style={{ background: T.cardSubtle, border: `1px dashed ${T.borderStrong}`, color: T.text2 }}>
              <Upload size={20} style={{ color: T.gold }} /> Click to upload an image
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="render-file-input" />

          {/* Canva bridge */}
          <div className="mb-4 rounded-lg p-3" style={{ background: T.cardSubtle, border: `1px solid ${T.border}` }}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold" style={{ color: T.text }}>Pull from Canva</span>
              <span className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                style={canvaConnected ? { background: `${T.success}22`, color: T.success } : { background: `${T.muted}22`, color: T.muted }}>
                {canvaConnected ? 'Connected' : 'Not connected'}
              </span>
            </div>
            {canvaConnected ? (
              <div className="flex gap-2">
                <input data-testid="canva-url-input" value={canvaUrl} onChange={(e) => setCanvaUrl(e.target.value)}
                  placeholder="Paste a Canva design link" className="flex-1 rounded-md px-2 py-1.5 text-xs outline-none"
                  style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text }} />
                <button data-testid="canva-import-btn" onClick={importFromCanva} disabled={importing}
                  className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold disabled:opacity-60" style={{ background: T.goldGrad, color: T.onGold }}>
                  {importing ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />} Pull
                </button>
              </div>
            ) : (
              <button data-testid="canva-connect-btn" onClick={connectCanva} className="w-full rounded-md px-3 py-1.5 text-xs font-semibold" style={{ background: T.chip, color: T.text, border: `1px solid ${T.border}` }}>
                Connect Canva
              </button>
            )}
          </div>

          <div className="mb-2 text-sm font-semibold" style={{ color: T.text }}>2 · Describe the look</div>
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid="style-presets">
            {STYLE_PRESETS.map((p) => (
              <button key={p.label} type="button" data-testid={`preset-${p.label.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => applyPreset(p)}
                className="rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all hover:opacity-90"
                style={{ background: T.chip, color: T.gold, border: `1px solid ${T.border}` }}>
                {p.label}
              </button>
            ))}
          </div>
          <textarea data-testid="render-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
            placeholder='e.g. "warm modern, walnut floors, cream boucle sofa, brass sconces, sheer linen drapes"'
            className="mb-3 w-full resize-none rounded-lg px-3 py-2 text-sm outline-none"
            style={{ background: T.cardSubtle, border: `1px solid ${T.border}`, color: T.text }} />
          <input data-testid="render-room" value={roomName} onChange={(e) => setRoomName(e.target.value)} placeholder="Room name (optional)"
            className="mb-4 w-full rounded-lg px-3 py-2 text-sm outline-none" style={{ background: T.cardSubtle, border: `1px solid ${T.border}`, color: T.text }} />

          <button data-testid="render-generate-btn" onClick={doRender} disabled={rendering}
            className="flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm font-bold disabled:opacity-60" style={{ background: T.goldGrad, color: T.onGold }}>
            {rendering ? <><Loader2 size={16} className="animate-spin" /> Rendering… (~20s)</> : <><Sparkles size={16} /> Generate rendering</>}
          </button>
          {error && <div data-testid="render-error" className="mt-3 rounded-lg p-2 text-xs" style={{ background: `${T.danger}18`, color: T.danger, border: `1px solid ${T.danger}55` }}>{error}</div>}
        </div>
      </div>

      {/* Gallery */}
      <div className="lg:col-span-8">
        <div className="mb-3 text-sm font-semibold" style={{ color: T.text }}>Renderings {renders.length ? `(${renders.length})` : ''}</div>
        {renders.length === 0 ? (
          <div className="rounded-xl p-12 text-center" style={{ background: T.card, border: `1px dashed ${T.border}`, color: T.muted }}>
            Your renderings will appear here. Upload a photo or Canva comp, describe the look, and hit Generate.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renders.map((r) => (
              <div key={r.id} data-testid={`render-card-${r.id}`} className="overflow-hidden rounded-xl" style={{ background: T.card, border: `1px solid ${T.border}` }}>
                <img src={fullUrl(r.image_url)} alt={r.room_name || 'rendering'} className="h-56 w-full object-cover" style={{ background: '#111' }} />
                <div className="p-3">
                  <div className="mb-1 flex items-center gap-2">
                    {r.room_name && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${T.gold}22`, color: T.gold }}>{r.room_name}</span>}
                    {r.had_input && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${T.purple || T.gold}22`, color: T.purple || T.gold }}>from photo</span>}
                  </div>
                  <div className="mb-2 line-clamp-2 text-xs" style={{ color: T.text2 }}>{r.prompt || r.prompt_used}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <a href={fullUrl(r.image_url)} download target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold" style={{ background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>
                      <Download size={12} /> Download
                    </a>
                    <button data-testid={`render-canva-${r.id}`} onClick={() => sendToCanva(r)} disabled={canvaBusy === r.id} className="flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold disabled:opacity-60" style={{ background: `${T.gold}22`, color: T.gold, border: `1px solid ${T.border}` }}>
                      {canvaBusy === r.id ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />} Send to Canva
                    </button>
                    <button data-testid={`render-delete-${r.id}`} onClick={() => del(r.id)} className="flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold" style={{ background: `${T.danger}18`, color: T.danger }}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------- ASSISTANT -------------------------------- */
const AssistantTab = ({ T, projectId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setInput(''); setSending(true);
    try {
      const r = await fetch(`${API}/ai-studio/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId, project_id: projectId || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Chat failed');
      setSessionId(d.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', text: d.reply }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'assistant', text: `⚠️ ${e.message || e}` }]);
    } finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-3xl rounded-xl" style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <div className="flex h-[62vh] flex-col">
        <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="assistant-messages">
          {messages.length === 0 && (
            <div className="mt-10 text-center text-sm" style={{ color: T.muted }}>
              <Bot size={28} className="mx-auto mb-3" style={{ color: T.gold }} />
              Ask about finishes, layouts, sourcing, palettes — anything design. {projectId ? "I know this project's details." : ''}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm"
                style={m.role === 'user'
                  ? { background: T.goldGrad, color: T.onGold }
                  : { background: T.cardSubtle, color: T.text, border: `1px solid ${T.border}` }}>
                {m.text}
              </div>
            </div>
          ))}
          {sending && <div className="flex justify-start"><div className="rounded-2xl px-4 py-2.5 text-sm" style={{ background: T.cardSubtle, color: T.muted }}><Loader2 size={14} className="animate-spin" /></div></div>}
          <div ref={endRef} />
        </div>
        <div className="flex gap-2 border-t p-3" style={{ borderColor: T.border }}>
          <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Ask your design assistant…"
            className="flex-1 rounded-lg px-3 py-2.5 text-sm outline-none" style={{ background: T.cardSubtle, border: `1px solid ${T.border}`, color: T.text }} />
          <button data-testid="assistant-send" onClick={send} disabled={sending}
            className="flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-bold disabled:opacity-60" style={{ background: T.goldGrad, color: T.onGold }}>
            <Send size={15} /> Send
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------- DESCRIBE --------------------------------- */
const DescribeTab = ({ T }) => {
  const [f, setF] = useState({ name: '', vendor: '', room: '', link: '' });
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const gen = async () => {
    if (!f.name.trim()) { setError('Enter an item name first.'); return; }
    setBusy(true); setError(''); setDesc('');
    try {
      const r = await fetch(`${API}/ai-studio/item-description`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setDesc(d.description || '');
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(desc); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const field = (k, ph) => (
    <input data-testid={`desc-${k}`} value={f[k]} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} placeholder={ph}
      className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={{ background: T.cardSubtle, border: `1px solid ${T.border}`, color: T.text }} />
  );

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-xl p-5" style={{ background: T.card, border: `1px solid ${T.border}` }}>
        <div className="mb-4 space-y-3">
          {field('name', 'Product name * (e.g. Boucle Swivel Chair)')}
          {field('vendor', 'Vendor (e.g. Four Hands)')}
          {field('room', 'Room (e.g. Living Room)')}
          {field('link', 'Product link (optional)')}
        </div>
        <button data-testid="desc-generate" onClick={gen} disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm font-bold disabled:opacity-60" style={{ background: T.goldGrad, color: T.onGold }}>
          {busy ? <><Loader2 size={16} className="animate-spin" /> Writing…</> : <><Wand2 size={16} /> Generate description</>}
        </button>
        {error && <div className="mt-3 rounded-lg p-2 text-xs" style={{ background: `${T.danger}18`, color: T.danger }}>{error}</div>}
      </div>
      <div className="rounded-xl p-5" style={{ background: T.card, border: `1px solid ${T.border}` }}>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-semibold" style={{ color: T.text }}>Description</span>
          {desc && (
            <button data-testid="desc-copy" onClick={copy} className="flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold" style={{ background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          )}
        </div>
        <div data-testid="desc-output" className="min-h-[180px] whitespace-pre-wrap rounded-lg p-3 text-sm" style={{ background: T.cardSubtle, color: desc ? T.text : T.muted, border: `1px solid ${T.border}` }}>
          {desc || 'The generated description and spec bullets will appear here.'}
        </div>
      </div>
    </div>
  );
};
